/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { INITIALIZER_PROGRAM_ADDRESS } from '../programs';

export const REPLACE_FEE_BENEFICIARY_DISCRIMINATOR = new Uint8Array([
  125, 194, 227, 81, 175, 100, 78, 200,
]);

export function getReplaceFeeBeneficiaryDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    REPLACE_FEE_BENEFICIARY_DISCRIMINATOR,
  );
}

export type ReplaceFeeBeneficiaryInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountLaunch extends string | AccountMeta<string> = string,
  TAccountLaunchFeeState extends string | AccountMeta<string> = string,
  TAccountCurrentBeneficiary extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? ReadonlyAccount<TAccountConfig>
        : TAccountConfig,
      TAccountLaunch extends string
        ? ReadonlyAccount<TAccountLaunch>
        : TAccountLaunch,
      TAccountLaunchFeeState extends string
        ? WritableAccount<TAccountLaunchFeeState>
        : TAccountLaunchFeeState,
      TAccountCurrentBeneficiary extends string
        ? ReadonlySignerAccount<TAccountCurrentBeneficiary> &
            AccountSignerMeta<TAccountCurrentBeneficiary>
        : TAccountCurrentBeneficiary,
      ...TRemainingAccounts,
    ]
  >;

export type ReplaceFeeBeneficiaryInstructionData = {
  discriminator: ReadonlyUint8Array;
  newBeneficiary: Address;
};

export type ReplaceFeeBeneficiaryInstructionDataArgs = {
  newBeneficiary: Address;
};

export function getReplaceFeeBeneficiaryInstructionDataEncoder(): FixedSizeEncoder<ReplaceFeeBeneficiaryInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['newBeneficiary', getAddressEncoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: REPLACE_FEE_BENEFICIARY_DISCRIMINATOR,
    }),
  );
}

export function getReplaceFeeBeneficiaryInstructionDataDecoder(): FixedSizeDecoder<ReplaceFeeBeneficiaryInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['newBeneficiary', getAddressDecoder()],
  ]);
}

export function getReplaceFeeBeneficiaryInstructionDataCodec(): FixedSizeCodec<
  ReplaceFeeBeneficiaryInstructionDataArgs,
  ReplaceFeeBeneficiaryInstructionData
> {
  return combineCodec(
    getReplaceFeeBeneficiaryInstructionDataEncoder(),
    getReplaceFeeBeneficiaryInstructionDataDecoder(),
  );
}

export type ReplaceFeeBeneficiaryAsyncInput<
  TAccountConfig extends string = string,
  TAccountLaunch extends string = string,
  TAccountLaunchFeeState extends string = string,
  TAccountCurrentBeneficiary extends string = string,
> = {
  config?: Address<TAccountConfig>;
  launch: Address<TAccountLaunch>;
  launchFeeState?: Address<TAccountLaunchFeeState>;
  currentBeneficiary: TransactionSigner<TAccountCurrentBeneficiary>;
  newBeneficiary: ReplaceFeeBeneficiaryInstructionDataArgs['newBeneficiary'];
};

export async function getReplaceFeeBeneficiaryInstructionAsync<
  TAccountConfig extends string,
  TAccountLaunch extends string,
  TAccountLaunchFeeState extends string,
  TAccountCurrentBeneficiary extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: ReplaceFeeBeneficiaryAsyncInput<
    TAccountConfig,
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountCurrentBeneficiary
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  ReplaceFeeBeneficiaryInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountCurrentBeneficiary
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    launch: { value: input.launch ?? null, isWritable: false },
    launchFeeState: { value: input.launchFeeState ?? null, isWritable: true },
    currentBeneficiary: {
      value: input.currentBeneficiary ?? null,
      isWritable: false,
    },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.config.value) {
    accounts.config.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(new Uint8Array([99, 111, 110, 102, 105, 103])),
      ],
    });
  }
  if (!accounts.launchFeeState.value) {
    accounts.launchFeeState.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            108, 97, 117, 110, 99, 104, 95, 102, 101, 101, 95, 115, 116, 97,
            116, 101,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'launch',
            accounts.launch.value,
          ),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('launchFeeState', accounts.launchFeeState),
      getAccountMeta('currentBeneficiary', accounts.currentBeneficiary),
    ],
    data: getReplaceFeeBeneficiaryInstructionDataEncoder().encode(
      args as ReplaceFeeBeneficiaryInstructionDataArgs,
    ),
    programAddress,
  } as ReplaceFeeBeneficiaryInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountCurrentBeneficiary
  >);
}

export type ReplaceFeeBeneficiaryInput<
  TAccountConfig extends string = string,
  TAccountLaunch extends string = string,
  TAccountLaunchFeeState extends string = string,
  TAccountCurrentBeneficiary extends string = string,
> = {
  config: Address<TAccountConfig>;
  launch: Address<TAccountLaunch>;
  launchFeeState: Address<TAccountLaunchFeeState>;
  currentBeneficiary: TransactionSigner<TAccountCurrentBeneficiary>;
  newBeneficiary: ReplaceFeeBeneficiaryInstructionDataArgs['newBeneficiary'];
};

export function getReplaceFeeBeneficiaryInstruction<
  TAccountConfig extends string,
  TAccountLaunch extends string,
  TAccountLaunchFeeState extends string,
  TAccountCurrentBeneficiary extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: ReplaceFeeBeneficiaryInput<
    TAccountConfig,
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountCurrentBeneficiary
  >,
  config?: { programAddress?: TProgramAddress },
): ReplaceFeeBeneficiaryInstruction<
  TProgramAddress,
  TAccountConfig,
  TAccountLaunch,
  TAccountLaunchFeeState,
  TAccountCurrentBeneficiary
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    launch: { value: input.launch ?? null, isWritable: false },
    launchFeeState: { value: input.launchFeeState ?? null, isWritable: true },
    currentBeneficiary: {
      value: input.currentBeneficiary ?? null,
      isWritable: false,
    },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('launchFeeState', accounts.launchFeeState),
      getAccountMeta('currentBeneficiary', accounts.currentBeneficiary),
    ],
    data: getReplaceFeeBeneficiaryInstructionDataEncoder().encode(
      args as ReplaceFeeBeneficiaryInstructionDataArgs,
    ),
    programAddress,
  } as ReplaceFeeBeneficiaryInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountLaunch,
    TAccountLaunchFeeState,
    TAccountCurrentBeneficiary
  >);
}

export type ParsedReplaceFeeBeneficiaryInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    launch: TAccountMetas[1];
    launchFeeState: TAccountMetas[2];
    currentBeneficiary: TAccountMetas[3];
  };
  data: ReplaceFeeBeneficiaryInstructionData;
};

export function parseReplaceFeeBeneficiaryInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedReplaceFeeBeneficiaryInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 4) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 4,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      config: getNextAccount(),
      launch: getNextAccount(),
      launchFeeState: getNextAccount(),
      currentBeneficiary: getNextAccount(),
    },
    data: getReplaceFeeBeneficiaryInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
